import { createContext, useContext, useState } from 'react'
import { createPortal } from 'react-dom'
import { observer } from 'mobx-react-lite'
import { Badge, Button, Empty, Spin, Tooltip, theme } from 'antd'
import {
    DndContext,
    DragOverlay,
    KeyboardSensor,
    PointerSensor,
    useDroppable,
    useSensor,
    useSensors,
} from '@dnd-kit/core'
import { useDraggable } from '@dnd-kit/core'
import { MdDeleteOutline } from 'react-icons/md'
import { TbMinus, TbPlus } from 'react-icons/tb'

import './Kanban.css'

import type { KanbanItemBase, KanbanStore } from './KanbanStore'
import type { CSSProperties, ReactNode } from 'react'
import type { DragEndEvent, DragStartEvent } from '@dnd-kit/core'

/** Псевдо-ціль смітника у onDrop. */
export const KANBAN_TRASH = 'trash'

/** Межі й крок масштабу — спільні для контролу і для збереження вибору. */
export const KANBAN_SCALE = { min: 1, max: 2, step: 0.25, default: 1.5 } as const

/**
 * Поточний масштаб для доменної картки.
 *
 * `renderCard` малює домен, а розміри всередині картки теж мають рости — тож
 * замість зміни сигнатури рендера множник їде контекстом. Працює і в
 * DragOverlay: портал зберігає React-дерево, отже й провайдер.
 */
const KanbanScaleContext = createContext<number>(1)

/** Множник масштабу канбану (1 — базовий розмір). */
export const useKanbanScale = (): number => useContext(KanbanScaleContext)

export interface KanbanDropEvent<T extends KanbanItemBase> {
    item: T
    /** Колонка-джерело. */
    from: string
    /** Колонка-ціль або KANBAN_TRASH. */
    to: string
}

interface KanbanBaseProps<T extends KanbanItemBase> {
    store: KanbanStore<T>
    /** Вміст картки — чистий доменний рендер (без dnd-обв'язки). */
    renderCard: (item: T) => ReactNode
    /** Клік по картці (не drag — PointerSensor з дистанцією їх розрізняє). */
    onItemOpen?: (item: T) => void
    /** Показувати зону «Видалити» під час перетягування (у `readOnly` ігнорується). */
    withTrash?: boolean
    /** Висота стрічки колонок (за замовчуванням — на весь вільний екран). */
    height?: string
    /** Множник розмірів колонок і карток (1 — базовий). */
    scale?: number
    /** Заданий обробник вмикає контрол масштабу над стрічкою. */
    onScaleChange?: (scale: number) => void
}

/**
 * Пропси канбану: або перетягування з обов'язковим `onDrop`, або `readOnly`
 * без нього (Q2.9, `SPEC-022` §3.5 — канбани перевезень, де статус міняють
 * лише кнопки картки). TypeScript не дасть передати `onDrop` разом з
 * `readOnly` і не дасть забути `onDrop` без `readOnly`.
 */
export type KanbanProps<T extends KanbanItemBase> = KanbanBaseProps<T> &
    (
        | {
              readOnly?: false
              /**
               * Рішення про перехід ухвалює домен: валідація напрямку, права,
               * optimistic move / модалки — усе тут (через методи store).
               */
              onDrop: (event: KanbanDropEvent<T>) => void
          }
        | {
              /** Лише перегляд: без перетягування й смітника, клік відкриває картку. */
              readOnly: true
              onDrop?: never
          }
    )

/** Картка режиму `readOnly`: звичайний блок без dnd-обв'язки, клік — `onItemOpen`. */
const StaticCard = <T extends KanbanItemBase>({
    item,
    renderCard,
    onItemOpen,
}: {
    item: T
    renderCard: (item: T) => ReactNode
    onItemOpen?: ((item: T) => void) | undefined
}) => (
    <div style={{ cursor: 'pointer' }} onClick={() => onItemOpen?.(item)}>
        {renderCard(item)}
    </div>
)

/** Draggable-обгортка картки: dnd-обв'язка — тут, вміст — доменний. */
const DraggableCard = observer(
    <T extends KanbanItemBase>({
        item,
        isBusy,
        renderCard,
        onItemOpen,
    }: {
        item: T
        isBusy: boolean
        renderCard: (item: T) => ReactNode
        onItemOpen?: ((item: T) => void) | undefined
    }) => {
        const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
            id: item.id,
            data: { item },
            disabled: isBusy,
        })

        const style: CSSProperties = {
            cursor: 'grab',
            opacity: isDragging ? 0.35 : isBusy ? 0.6 : 1,
            userSelect: 'none',
        }

        return (
            <div
                ref={setNodeRef}
                style={style}
                {...attributes}
                {...listeners}
                onClick={() => {
                    if (!isDragging) onItemOpen?.(item)
                }}
            >
                {renderCard(item)}
            </div>
        )
    }
)

const Column = observer(
    <T extends KanbanItemBase>({
        columnKey,
        store,
        renderCard,
        onItemOpen,
        readOnly,
    }: {
        columnKey: string
        store: KanbanStore<T>
        renderCard: (item: T) => ReactNode
        onItemOpen?: ((item: T) => void) | undefined
        readOnly: boolean
    }) => {
        const { token } = theme.useToken()
        const scale = useKanbanScale()
        const px = (base: number): number => Math.round(base * scale)
        const { setNodeRef, isOver } = useDroppable({ id: columnKey })

        const def = store.defs.find((d) => d.key === columnKey)
        const col = store.columns[columnKey]
        if (!def || !col) return null

        return (
            <div
                ref={setNodeRef}
                style={{
                    display: 'flex',
                    flexDirection: 'column',
                    minWidth: px(264),
                    width: px(264),
                    maxHeight: '100%',
                    background: isOver ? token.colorPrimaryBg : token.colorFillQuaternary,
                    border: `1px solid ${isOver ? token.colorPrimaryBorder : token.colorBorderSecondary}`,
                    borderRadius: token.borderRadiusLG,
                    transition: 'background 0.15s, border-color 0.15s',
                }}
            >
                <div
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: px(8),
                        padding: `${px(10)}px ${px(12)}px`,
                        borderBottom: `1px solid ${token.colorBorderSecondary}`,
                    }}
                >
                    {def.color && (
                        <span
                            style={{
                                width: px(8),
                                height: px(8),
                                borderRadius: '50%',
                                background: def.color,
                                flexShrink: 0,
                            }}
                        />
                    )}
                    <span style={{ fontWeight: 600, fontSize: px(13) }}>{def.label}</span>
                    <Badge
                        count={col.total}
                        showZero
                        color={token.colorFillSecondary}
                        style={{ color: token.colorTextSecondary }}
                    />
                    {col.loading && <Spin size="small" style={{ marginLeft: 'auto' }} />}
                </div>

                <div
                    className="wl-kanban-scroll"
                    style={{
                        flex: 1,
                        overflowY: 'auto',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: px(8),
                        padding: px(10),
                    }}
                >
                    {col.items.length === 0 && !col.loading && (
                        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={false} style={{ margin: '24px 0' }} />
                    )}
                    {col.items.map((item) =>
                        readOnly ? (
                            <StaticCard key={item.id} item={item} renderCard={renderCard} onItemOpen={onItemOpen} />
                        ) : (
                            <DraggableCard
                                key={item.id}
                                item={item}
                                isBusy={store.movingId === item.id}
                                renderCard={renderCard}
                                onItemOpen={onItemOpen}
                            />
                        )
                    )}
                    {store.hasMore(columnKey) && (
                        <Button
                            size="small"
                            type="text"
                            loading={col.loading}
                            onClick={() => void store.loadMore(columnKey)}
                        >
                            Показати ще
                        </Button>
                    )}
                </div>
            </div>
        )
    }
)

/** Панель масштабу: «−», поточний відсоток, «+». */
const ScaleControl = ({ scale, onChange }: { scale: number; onChange: (scale: number) => void }) => {
    const { token } = theme.useToken()

    const clamp = (value: number): number =>
        Math.min(KANBAN_SCALE.max, Math.max(KANBAN_SCALE.min, Math.round(value * 100) / 100))

    return (
        <div className="wl-kanban-scale" style={{ display: 'flex', alignItems: 'center', gap: 4, marginBottom: 8 }}>
            <Tooltip title="Зменшити">
                <Button
                    size="small"
                    icon={<TbMinus />}
                    disabled={scale <= KANBAN_SCALE.min}
                    onClick={() => onChange(clamp(scale - KANBAN_SCALE.step))}
                />
            </Tooltip>
            <span
                style={{
                    minWidth: 46,
                    textAlign: 'center',
                    fontSize: 12,
                    color: token.colorTextSecondary,
                    userSelect: 'none',
                }}
            >
                {Math.round(scale * 100)}%
            </span>
            <Tooltip title="Збільшити">
                <Button
                    size="small"
                    icon={<TbPlus />}
                    disabled={scale >= KANBAN_SCALE.max}
                    onClick={() => onChange(clamp(scale + KANBAN_SCALE.step))}
                />
            </Tooltip>
        </div>
    )
}

/** Зона «Видалити» (ehs `Trash`): видима лише під час перетягування. */
const TrashZone = ({ visible }: { visible: boolean }) => {
    const { token } = theme.useToken()
    const { setNodeRef, isOver } = useDroppable({ id: KANBAN_TRASH })

    if (!visible) return null

    return (
        <div
            ref={setNodeRef}
            style={{
                position: 'fixed',
                left: '50%',
                bottom: 24,
                transform: 'translateX(-50%)',
                zIndex: 1100,
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                padding: '12px 28px',
                borderRadius: token.borderRadiusLG,
                border: `2px dashed ${isOver ? token.colorError : token.colorBorder}`,
                background: isOver ? token.colorErrorBg : token.colorBgElevated,
                color: isOver ? token.colorError : token.colorTextSecondary,
                boxShadow: token.boxShadowSecondary,
                fontSize: 14,
            }}
        >
            <MdDeleteOutline style={{ fontSize: 18 }} />
            Видалити
        </div>
    )
}

/**
 * Генерик-канбан (Ship) — перенос каркаса ehs `Ship/Components/Kanban` на
 * dnd-kit без домену: колонки з описів стора, по-колонкова пагінація,
 * DragOverlay у порталі, опційний смітник. Що таке «перехід» і що робити на
 * drop — вирішує доменний адаптер через `onDrop` (пор. TaskKanban:
 * матриця статусів + воркфлоу-роути + модалка часу).
 *
 * `readOnly` (Q2.9): `DndContext` не рендериться, картки — звичайні блоки,
 * клік відкриває картку; пагінація колонок і масштаб — як завжди.
 */
export const Kanban = observer(<T extends KanbanItemBase>(props: KanbanProps<T>) => {
    const { store, renderCard, onItemOpen, withTrash = true, height, scale = 1, onScaleChange } = props
    const readOnly = props.readOnly === true
    const [activeItem, setActiveItem] = useState<T | null>(null)

    const sensors = useSensors(
        // 6px дистанції: короткий клік лишається кліком (відкриває картку)
        useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
        useSensor(KeyboardSensor)
    )

    const handleDragStart = (event: DragStartEvent): void => {
        const item = (event.active.data.current as { item?: T } | undefined)?.item
        setActiveItem(item ?? null)
    }

    const handleDragEnd = (event: DragEndEvent): void => {
        const item = activeItem
        setActiveItem(null)

        const to = event.over?.id as string | undefined
        if (!item || !to) return

        const from = store.columnKeys.find((key) => store.columns[key]?.items.some((i) => i.id === item.id))
        if (!from || from === to) return

        if (props.readOnly !== true) props.onDrop({ item, from, to })
    }

    const board = (
        <div
            className="wl-kanban-scroll"
            style={{
                display: 'flex',
                gap: Math.round(12 * scale),
                alignItems: 'flex-start',
                overflowX: 'auto',
                paddingBottom: 8,
                minHeight: 420,
                height: height ?? 'calc(100vh - 260px)',
            }}
        >
            {store.columnKeys.map((key) => (
                <Column
                    key={key}
                    columnKey={key}
                    store={store}
                    renderCard={renderCard}
                    onItemOpen={onItemOpen}
                    readOnly={readOnly}
                />
            ))}
        </div>
    )

    return (
        <KanbanScaleContext.Provider value={scale}>
            <div style={{ width: '100%' }}>
                {onScaleChange && <ScaleControl scale={scale} onChange={onScaleChange} />}
                {readOnly ? (
                    board
                ) : (
                    <DndContext
                        sensors={sensors}
                        onDragStart={handleDragStart}
                        onDragEnd={handleDragEnd}
                        onDragCancel={() => setActiveItem(null)}
                    >
                        {board}

                        {withTrash && <TrashZone visible={activeItem !== null} />}

                        {createPortal(
                            <DragOverlay>
                                {activeItem ? (
                                    <div style={{ cursor: 'grabbing', width: Math.round(244 * scale) }}>
                                        {renderCard(activeItem)}
                                    </div>
                                ) : null}
                            </DragOverlay>,
                            document.body
                        )}
                    </DndContext>
                )}
            </div>
        </KanbanScaleContext.Provider>
    )
})
